import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CategoriaDaCasa, ConviteDeCasa, EstadoDaCasa, ItemDaCasa, MoradorDaCasa, VisaoDaCasa,
  cancelarConvite, convidarParaCasa, getCasa, getGrupo, responderConvite, sairDaCasa,
} from '../services/api';
import { brlOrMasked } from '../utils/format';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { useConfirmar } from '../contexts/Confirmacao';
import { useBotaoOcupado } from '../utils/botaoOcupado';
import './NossaConta.css';

/**
 * Nossa conta — para onde foi o dinheiro da casa.
 *
 * ── Para quem esta tela é ────────────────────────────────────────────────
 *
 * Para quem NÃO controla o dinheiro. Em casais e famílias uma pessoa tem o
 * mapa e a outra vive no território sem ele, e o custo disso não é tempo
 * perdido: é briga. Nas palavras dela, "eles não querem falar sobre isso,
 * querem que na plataforma tudo fique claro para não discutirem".
 *
 * Então o produto não é juntar dois boards. É um terceiro neutro que segura os
 * fatos, para que ninguém precise ser quem puxa o assunto.
 *
 * ── O que a tela mostra, e o que ela recusa mostrar ──────────────────────
 *
 * Mostra para onde foi: por categoria, com os maiores itens. Não mostra quem
 * gastou o quê — isso daria placar à discussão que a feature existe para
 * evitar.
 *
 * A entrada é a exceção: quanto cada um pôs aparece, por escolha dela. A conta
 * precisa fechar, e "entrou X" sem dizer de onde deixaria no ar exatamente a
 * pergunta que a tela deveria ter tornado desnecessária.
 *
 * Gasto pessoal de ninguém aparece — nem somado, nem como "R$ 800 (privado)".
 * Buraco com etiqueta anuncia que existe algo escondido e convida a pergunta.
 * O pessoal não é um item omitido daqui; ele não pertence a esta tela.
 */

type Valor = (n: number) => string;

/**
 * O saldo da casa e o detalhe dela — fora do componente principal desde 05/10
 * para o EXEMPLO da tela vazia ser desenhado pelo mesmo código da tela cheia.
 * Um exemplo com marcação própria envelhece: a tela muda e ele continua
 * mostrando como ela era.
 */
/*
 * Entrou, saiu, sobrou — os três lado a lado (05/10, pedido dela: "que nem no
 * exemplo da página de apresentação").
 *
 * A versão anterior tinha UMA manchete, o que sobrou, com a conta em texto
 * embaixo: três números do mesmo tamanho disputavam o olho. A resposta a isso
 * não foi voltar a três iguais — o terceiro continua sendo o destaque: é o
 * único com cor, verde quando sobra e vermelho quando falta, e o rótulo dele
 * muda junto. Os outros dois ficam neutros.
 */
const ResumoDaCasa: React.FC<{ casa: VisaoDaCasa; valor: Valor }> = ({ casa, valor }) => {
  const faltou = casa.sobrou < 0;
  return (
    <div className="nc-tres" role="group" aria-label="O mês da casa">
      <div className="nc-tres-caixa">
        <span className="nc-tres-rotulo">Entrou</span>
        <strong className="nc-tres-valor">{valor(casa.entrou)}</strong>
      </div>
      <div className="nc-tres-caixa">
        <span className="nc-tres-rotulo">Saiu</span>
        <strong className="nc-tres-valor">{valor(casa.saiu)}</strong>
      </div>
      <div className={`nc-tres-caixa nc-tres-saldo${faltou ? ' faltou' : ''}`}>
        <span className="nc-tres-rotulo">{faltou ? 'Faltou' : 'Sobrou'}</span>
        <strong className="nc-tres-valor">{valor(Math.abs(casa.sobrou))}</strong>
      </div>
    </div>
  );
};

const DetalheDaCasa: React.FC<{ casa: VisaoDaCasa; valor: Valor }> = ({ casa, valor }) => (
  <>
    {/*
      * Quem pôs quanto. É a decisão mais delicada do desenho, e ela
      * é dela: sem isso, "entrou X" não diz de onde, e a pergunta que
      * a tela deveria matar continua viva.
      */}
    <section className="nc-bloco">
      <h3>Quem pôs no bolo</h3>
      <ul className="nc-lista">
        {casa.moradores.map((m: MoradorDaCasa) => (
          <li key={m.userId}>
            <span className="nc-desc">{m.nome}</span>
            <span className="nc-valor">{valor(m.entrou)}</span>
          </li>
        ))}
      </ul>
    </section>

    {casa.categorias.length > 0 && (
      <section className="nc-bloco">
        <h3>Para onde foi</h3>
        <ul className="nc-categorias">
          {casa.categorias.map((c: CategoriaDaCasa) => (
            <li key={c.nome}>
              <div className="nc-cat-topo">
                <span className="nc-cat-nome">
                  <span className="nc-cat-cor" style={{ background: c.cor }} />
                  <span>{c.nome}</span>
                </span>
                <span className="nc-valor">{valor(c.total)}</span>
              </div>
              {/*
                * A barra embaixo, e não numa coluna do lado. Em 375px
                * a versão em grade espremia o nome a ponto de cortar
                * "Alimentação" — e a barra é justamente o que dá para
                * empilhar sem perder nada.
                */}
              <div className="nc-barra-bg">
                <div
                  className="nc-barra-fill"
                  style={{
                    width: `${Math.max((c.total / casa.saiu) * 100, 2)}%`,
                    background: c.cor,
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      </section>
    )}

    {casa.maiores.length > 0 && (
      <section className="nc-bloco">
        {/*
          * Sem a coluna de quem lançou. O dado vem do servidor para o
          * filtro que o doc previu, mas mostrar por padrão é dar
          * placar à discussão que a feature existe para evitar.
          */}
        <h3>Os maiores</h3>
        <ul className="nc-lista">
          {casa.maiores.map((i: ItemDaCasa) => (
            <li key={i.id}>
              <span className="nc-desc">{i.descricao}</span>
              <span className="nc-valor">{valor(i.valor)}</span>
            </li>
          ))}
        </ul>
      </section>
    )}
  </>
);

/**
 * Uma casa de mentira, para mostrar como a tela fica (pedido dela, 05/10).
 *
 * As outras seções do app já mostram um "Exemplo" esmaecido quando estão
 * vazias; esta só tinha um parágrafo e um campo de email, e ninguém convida
 * outra pessoa para uma tela que não consegue imaginar.
 *
 * Os números FECHAM, como os de verdade: 5.500 + 4.000 entram, as cinco
 * categorias somam o que saiu, e a diferença é o que sobrou. Um exemplo cuja
 * conta não bate ensinaria a desconfiar justamente da tela que existe para
 * ninguém precisar conferir.
 */
const CASA_DE_EXEMPLO: VisaoDaCasa = {
  casa: { id: 'exemplo' },
  mes: '',
  entrou: 9500,
  saiu: 8260,
  sobrou: 1240,
  moradores: [
    { userId: 'exemplo-1', nome: 'Você', entrou: 5500 },
    { userId: 'exemplo-2', nome: 'Ana', entrou: 4000 },
  ],
  categorias: [
    { nome: 'Moradia', cor: '#7c3aed', total: 3200 },
    { nome: 'Mercado', cor: '#16a34a', total: 1850 },
    { nome: 'Lazer', cor: '#ea580c', total: 1510 },
    { nome: 'Contas', cor: '#2563eb', total: 980 },
    { nome: 'Transporte', cor: '#0891b2', total: 720 },
  ],
  maiores: [
    { id: 'exemplo-a', descricao: 'Aluguel', valor: 2800, categoria: 'Moradia', de: '' },
    { id: 'exemplo-b', descricao: 'Mercado do mês', valor: 640, categoria: 'Mercado', de: '' },
    { id: 'exemplo-c', descricao: 'Conta de luz', valor: 310, categoria: 'Contas', de: '' },
  ],
  lancamentos: 24,
  quantosMoradores: 2,
};

/**
 * O exemplo, esmaecido e sem toque — o mesmo padrão de `empty-preview-*` das
 * outras seções (`App.css`). `aria-hidden`: é ilustração, e um leitor de tela
 * anunciaria "sobrou na casa R$ 1.240" como se fosse dado dela.
 */
const ExemploDaCasa: React.FC<{ valor: Valor; comResumo?: boolean }> = ({ valor, comResumo = true }) => (
  <div className="empty-preview-wrap nc-exemplo">
    <span className="empty-preview-badge">Exemplo</span>
    <div className="empty-preview-ghost" aria-hidden="true">
      {comResumo && <ResumoDaCasa casa={CASA_DE_EXEMPLO} valor={valor} />}
      <DetalheDaCasa casa={CASA_DE_EXEMPLO} valor={valor} />
    </div>
  </div>
);

interface Props {
  hideValues?: boolean;
  /** O mês que o quadro está exibindo, para a casa falar do mesmo. */
  mes?: string;
  /**
   * Sobe sempre que um lançamento é gravado, em qualquer canto do app.
   *
   * A soma da casa é feita de lançamentos, então ela envelhece junto com eles.
   * Sem isto a seção só se atualizava ao trocar de mês — quem marcasse um gasto
   * como da casa via o cartão parado e concluía que a marca não pegou.
   */
  refreshTrigger?: number;
}

const NossaConta: React.FC<Props> = ({ hideValues = false, mes, refreshTrigger }) => {
  const [casa, setCasa] = useState<VisaoDaCasa | null>(null);
  const [grupo, setGrupo] = useState<EstadoDaCasa | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [email, setEmail] = useState('');
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  // Aceitar, recusar, chamar, cancelar e sair dividem `ocupado`: quem foi tocado se enche.
  const tela = useRef<HTMLDivElement>(null);
  useBotaoOcupado(tela, ocupado);
  const confirmar = useConfirmar();

  /*
   * De que mês é a casa na tela (15/09). Revalidar o MESMO mês mantém os
   * números enquanto os novos chegam — trocar tudo por "Carregando a casa..."
   * a cada volta da aba fazia a página pular. Mês diferente mostra o
   * carregando: número de outro mês na tela, nem por um instante.
   */
  const [mesCarregado, setMesCarregado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [v, g] = await Promise.all([getCasa(mes), getGrupo()]);
      setCasa(v);
      setGrupo(g);
      setMesCarregado(mes ?? '');
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui carregar a casa.'));
    } finally {
      setCarregando(false);
    }
    /*
     * `refreshTrigger` entra nas dependências sem ser usado no corpo — é o
     * jeito deste projeto de dizer "refaça quando isto mudar", e é o mesmo que
     * `PaymentMethods` faz. O lint pediria para tirar; tirar é o bug.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, refreshTrigger]);

  useEffect(() => { carregar(); }, [carregar]);

  /*
   * `brlOrMasked` e não uma máscara própria: o "olhinho" que esconde valores é
   * global, e uma segunda implementação dele viraria a tela onde os valores
   * continuam visíveis depois de a pessoa mandar escondê-los.
   */
  const valor = (n: number) => brlOrMasked(n, hideValues);

  const convidar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setOcupado(true);
    try {
      await convidarParaCasa(email.trim());
      setEmail('');
      await carregar();
    } catch (e2) {
      setErro(mensagemDeErro(e2, 'Não consegui enviar o convite.'));
    } finally {
      setOcupado(false);
    }
  };

  const responder = async (c: ConviteDeCasa, resposta: 'aceitar' | 'recusar') => {
    if (resposta === 'aceitar' && !await confirmar({
      titulo: 'Entrar nesta casa?',
      alvo: c.de,
      /*
       * A confirmação diz as DUAS direções.
       *
       * Aceitar não é só ganhar acesso ao dinheiro do outro — é abrir o seu na
       * mesma medida. Um diálogo que só falasse do que se ganha estaria
       * vendendo metade do acordo.
       */
      texto: `Vocês dois passam a ver os gastos que cada um marcar como da casa. `
        + 'O que você marcar como pessoal continua só seu, e não aparece nem somado. '
        + 'Dá para sair quando quiser.',
      acao: 'Entrar',
    })) return;

    setOcupado(true);
    try {
      await responderConvite(c.id, resposta);
      await carregar();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui registrar a sua resposta.'));
    } finally {
      setOcupado(false);
    }
  };

  const sair = async () => {
    if (!await confirmar({
      titulo: 'Sair da casa?',
      texto: 'Vocês param de ver os gastos de casa um do outro. Seus lançamentos '
        + 'continuam todos com você, e nada é apagado.',
      acao: 'Sair', perigo: true,
    })) return;
    setOcupado(true);
    try {
      await sairDaCasa();
      await carregar();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui tirar você da casa.'));
    } finally {
      setOcupado(false);
    }
  };

  const cancelar = async (m: { id: string; nome: string | null; email: string | null }) => {
    if (!await confirmar({
      titulo: 'Cancelar este convite?',
      alvo: m.nome ?? m.email ?? 'essa pessoa',
      texto: 'Ela deixa de ver o convite. Dá para chamar de novo depois.',
      acao: 'Cancelar convite', perigo: true,
    })) return;
    setOcupado(true);
    try {
      await cancelarConvite(m.id);
      await carregar();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui cancelar o convite.'));
    } finally {
      setOcupado(false);
    }
  };

  if (carregando && mesCarregado !== (mes ?? '')) {
    return (
      <div className="loading-wrap">
        <div className="loading-ring" />
        <span className="loading-text">Carregando a casa...</span>
      </div>
    );
  }

  const convites = grupo?.convites ?? [];
  const pendentes = (grupo?.membros ?? []).filter(m => m.status === 'pendente');
  const naCasa = !!casa?.casa;

  const quantos = (grupo?.membros ?? []).filter(m => m.status === 'aceito').length;

  return (
    <div className="nc-container" ref={tela}>
      {/*
        * Cabeçalho próprio, como Métodos, Dívidas e Metas têm.
        *
        * O painel do quadro já mostra "Nossa conta" na barra que recolhe, e as
        * outras seções repetem o nome dentro mesmo assim — porque a barra some
        * quando a seção é aberta em tela cheia, e sem isto o conteúdo ficaria
        * sem título nenhum.
        */}
      <div className="nc-header">
        <h2>Nossa conta</h2>
        {naCasa && quantos > 0 && (
          <span className="nc-badge">
            {quantos === 1 ? 'só você' : `${quantos} pessoas`}
          </span>
        )}
      </div>

      {erro && <p className="nc-erro">{erro}</p>}

      {/*
        * Os convites que ME esperam vêm primeiro, sempre — mesmo quando já
        * estou numa casa. É a única coisa aqui que outra pessoa está
        * aguardando, e enterrá-la embaixo dos números faria alguém esperar
        * dias por um clique de dois segundos.
        */}
      {convites.length > 0 && (
        <div className="nc-convites">
          {convites.map(c => (
            <div key={c.id} className="nc-convite">
              <div className="nc-convite-texto">
                <strong>{c.de}</strong> quer dividir o dinheiro da casa com você.
                <span className="nc-convite-ajuda">
                  Vocês dois veem só o que cada um marcar como da casa.
                </span>
              </div>
              <div className="nc-convite-acoes">
                <button className="nc-btn-secundario" disabled={ocupado}
                  onClick={() => responder(c, 'recusar')}>Recusar</button>
                <button className="nc-btn" disabled={ocupado}
                  onClick={() => responder(c, 'aceitar')}>Entrar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!naCasa ? (
        <div className="nc-vazio">
          {/*
            * "Você", singular.
            *
            * "Vocês" pressupõe que já existe um vocês — e quem lê esta tela é
            * justamente quem ainda não tem com quem dividir. A frase falava com
            * um casal para alguém que pode estar sozinho ali.
            */}
          <h3>Você ainda não divide uma casa aqui</h3>
          <p>
            Quem mora com você passa a ver os gastos que vocês marcarem como
            <strong> da casa</strong>, e só esses. O que ficar como pessoal
            continua só seu, e não aparece nem somado.
          </p>
          {casa?.motivo === 'indisponivel' ? (
            <p className="nc-indisponivel">
              Esta parte ainda não está no ar. Volte em instantes.
            </p>
          ) : (
            <form className="nc-form" onSubmit={convidar}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email de quem mora com você"
                aria-label="Email de quem você quer chamar"
              />
              <button className="nc-btn" disabled={ocupado || !email.trim()}>
                Chamar
              </button>
            </form>
          )}
          <p className="nc-nota">
            A pessoa precisa ter conta no FinTracking, e decide se aceita.
          </p>
          {/* Depois do convite, não antes: o campo é a ação, e o exemplo é o
              que a convence a usá-la. Em cima, ele empurraria o campo para
              fora da primeira tela no celular. */}
          <ExemploDaCasa valor={valor} />
        </div>
      ) : (
        <>
          {/*
            * O saldo da casa, e ele FECHA: entrou − saiu = sobrou.
            *
            * Sem número órfão. Se a soma não batesse, a primeira leitura seria
            * que alguém escondeu alguma coisa — e aí a tela teria criado a
            * desconfiança que veio apagar.
            */}
          <ResumoDaCasa casa={casa!} valor={valor} />

          {casa!.lancamentos === 0 ? (
            /*
             * Vazio HONESTO: a casa existe, o mês é que não teve nada marcado.
             * Dizer "nenhum gasto" seria mentira — pode ter havido muitos, só
             * nenhum marcado como da casa. A diferença é a instrução que falta.
             */
            <>
            <p className="nc-vazio-mes">
              Nenhum lançamento marcado como <strong>da casa</strong> neste mês.
              Marque um método de pagamento como da casa em <em>Métodos</em>, e
              tudo que passar nele entra aqui.
            </p>
            <ExemploDaCasa valor={valor} comResumo={false} />
            </>
          ) : (
            <DetalheDaCasa casa={casa!} valor={valor} />
          )}

          <section className="nc-bloco nc-gente">
            <h3>Na casa</h3>
            <ul className="nc-lista">
              {(grupo?.membros ?? []).map(m => (
                <li key={m.id} className={m.status === 'pendente' ? 'nc-pendente' : ''}>
                  <span className="nc-membro-nome">
                    {m.nome ?? m.email}
                    {m.eu && <span className="nc-voce">você</span>}
                    {m.status === 'pendente' && <span className="nc-esperando">esperando resposta</span>}
                  </span>
                  {m.status === 'pendente' && !m.eu && (
                    <button className="nc-btn-x" disabled={ocupado}
                      onClick={() => cancelar(m)} aria-label="Cancelar convite">✕</button>
                  )}
                </li>
              ))}
            </ul>

            {grupo && (grupo.membros.length < grupo.teto) && pendentes.length + grupo.membros.length < grupo.teto && (
              <form className="nc-form" onSubmit={convidar}>
                <input
                  type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="chamar mais alguém"
                  aria-label="Email de quem você quer chamar"
                />
                <button className="nc-btn" disabled={ocupado || !email.trim()}>Chamar</button>
              </form>
            )}

            <button className="nc-sair" onClick={sair} disabled={ocupado}>
              Sair da casa
            </button>
          </section>
        </>
      )}
    </div>
  );
};

export default NossaConta;
